import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ContractHistory } from '@shared/schemas';

import {
  clientCategoriesTransitions,
  type ClientCategory,
} from '../../_shared/domain/licence-client-categories';
import type { LicenceCsm } from '../../_shared/domain/licence-csm';
import type { CreditsError } from '../domain/credit-error';
import type { CreditHistory } from '../domain/credit-ledger.model';
import type {
  Approver,
  LicenceActivityPage,
  LicenceDetail,
  LicenceNotes,
  ListMutationKind,
  ListResponse,
  PrimaryContact,
  UserResponse,
} from '../domain/licence-detail.model';
import { markUsersAsSent, sortListsByRecency } from '../domain/licence-detail.rules';
import type { ActivityState } from './licence-detail.state';
import {
  emptyActivity,
  emptyContactsSave,
  emptyContractHistory,
  emptyEdit,
  emptyGrant,
  emptyLicenceDelete,
  emptyLicenceStatus,
  emptyListMutation,
  emptyNotes,
  emptyUserArchive,
  emptyUserDelete,
  initialState,
} from './licence-detail.state';

/** Payload for `loadSucceeded`: everything the initial multi-resource load returns. */
export interface LoadedDetail {
  licence: LicenceDetail;
  users: UserResponse[];
  lists: ListResponse[];
}

/** Payload of the activity transitions: which licence the page belongs to. */
export interface ActivityPageLoaded {
  licenceId: string;
  page: LicenceActivityPage;
}

export interface ActivityPageFailed {
  licenceId: string;
  message: string;
}

/** Payload of the notes transitions (#605): which licence the notes belong to. */
export interface NotesLoaded {
  licenceId: string;
  notes: LicenceNotes;
}

/** Payload of the contract history transitions (#750). */
export interface ContractHistoryLoaded {
  licenceId: string;
  history: ContractHistory;
}

export interface NotesFailed {
  licenceId: string;
  message: string;
}

const isAwaitingMore = (activity: ActivityState, licenceId: string) =>
  activity.licenceId === licenceId && activity.moreStatus === 'loading';

/**
 * Admin Licence Detail slice. Reducers are pure state transitions only; every side
 * effect (the initial load, the mutations, the credit history load and grant)
 * lives in the saga, which dispatches these `load*` / mutation / `credits*` /
 * `grant*` transitions as it progresses. The one "edit" reducer
 * (`usersMarkedSent`) delegates to a pure domain helper.
 */
const slice = createSlice({
  name: 'licenceDetail',
  initialState,
  reducers: {
    // ── Load lifecycle ──
    // `loadStarted` keeps the existing `licence` so a re-load never blanks the page
    // (the page guards on `licence`, not on `loadStatus`).
    loadStarted: (state) => {
      state.loadStatus = 'loading';
    },
    loadSucceeded: (state, action: PayloadAction<LoadedDetail>) => {
      state.licence = action.payload.licence;
      state.users = action.payload.users;
      state.lists = action.payload.lists;
      state.loadStatus = 'loaded';
    },
    loadFailed: (state) => {
      state.loadStatus = 'failed';
    },

    // ── User deletion (Users tab) ──
    // The row leaves `users` on success, so every figure read from it (the tab's
    // summary and badge, "At a glance") moves with it, without a reload. A main
    // contact who no longer exists leaves the contacts too.
    userDeleteStarted: (state) => {
      state.userDelete = { status: 'pending', error: null };
    },
    userDeleteSucceeded: (state, action: PayloadAction<string>) => {
      state.users = state.users.filter((u) => u.id !== action.payload);
      if (state.licence) {
        state.licence.primary_contacts = state.licence.primary_contacts.filter(
          (c) => c.id !== action.payload,
        );
      }
      state.userDelete = { status: 'succeeded', error: null };
    },
    userDeleteFailed: (state, action: PayloadAction<string>) => {
      state.userDelete = { status: 'failed', error: action.payload };
    },
    userDeleteCleared: (state) => {
      state.userDelete = emptyUserDelete;
    },

    // ── Deactivate / reactivate (#573) ──
    // The row keeps its place and takes the saved `archived_at`, so its chip,
    // the summary and the filter all read the new state. Deactivating also takes
    // the person out of the main contacts, as the server does.
    userArchiveStarted: (state) => {
      state.userArchive = { status: 'pending', error: null };
    },
    userArchiveSucceeded: (
      state,
      action: PayloadAction<{ userId: string; archivedAt: string | null }>,
    ) => {
      const { userId, archivedAt } = action.payload;
      const user = state.users.find((u) => u.id === userId);
      if (user) user.archived_at = archivedAt;
      if (archivedAt && state.licence) {
        state.licence.primary_contacts = state.licence.primary_contacts.filter(
          (c) => c.id !== userId,
        );
      }
      state.userArchive = { status: 'succeeded', error: null };
    },
    userArchiveFailed: (state, action: PayloadAction<string>) => {
      state.userArchive = { status: 'failed', error: action.payload };
    },
    userArchiveCleared: (state) => {
      state.userArchive = emptyUserArchive;
    },

    // ── Edit licence lifecycle ──
    editStarted: (state) => {
      state.edit = { status: 'pending', error: null };
    },
    // On success, patch the loaded licence in place so the panel and the tabs show
    // the new name, category and country without a full reload.
    editSucceeded: (state, action: PayloadAction<LicenceDetail>) => {
      state.licence = action.payload;
      state.edit = { status: 'succeeded', error: null };
    },
    /**
     * A silent re-read of the licence after a list status change (#750). Only the
     * figures a list move changes are copied: a slower re-read must not undo an
     * Edit licence saved meanwhile.
     */
    licenceRefreshed: (state, action: PayloadAction<LicenceDetail>) => {
      if (!state.licence || state.licence.id !== action.payload.id) return;
      state.licence.profiles_delivered = action.payload.profiles_delivered;
      state.licence.last_list_sent_at = action.payload.last_list_sent_at;
      state.licence.last_admin_activity_at = action.payload.last_admin_activity_at;
    },
    editFailed: (state, action: PayloadAction<string>) => {
      state.edit = { status: 'failed', error: action.payload };
    },
    /** Acknowledge a settled edit (the dialog hook calls it after the snackbar). */
    editCleared: (state) => {
      state.edit = emptyEdit;
    },

    // ── Danger zone: licence status lifecycle (#582) ──
    licenceStatusStarted: (state) => {
      state.licenceStatus = { status: 'pending', error: null, revokedSessions: 0 };
    },
    // Patch the loaded licence in place, like the edit does: the status chip in the
    // identity rail and the Danger zone card must agree without a full reload.
    licenceStatusSucceeded: (
      state,
      action: PayloadAction<{ licence: LicenceDetail; revokedSessions: number }>,
    ) => {
      state.licence = action.payload.licence;
      state.licenceStatus = {
        status: 'succeeded',
        error: null,
        revokedSessions: action.payload.revokedSessions,
      };
    },
    licenceStatusFailed: (state, action: PayloadAction<string>) => {
      state.licenceStatus = { status: 'failed', error: action.payload, revokedSessions: 0 };
    },
    /** Acknowledge a settled status change (the hook calls it after the snackbar). */
    licenceStatusCleared: (state) => {
      state.licenceStatus = emptyLicenceStatus;
    },

    // ── Danger zone: licence deletion lifecycle (#584) ──
    licenceDeleteStarted: (state) => {
      state.licenceDelete = { status: 'pending', error: null };
    },
    // The loaded licence is left as it is: the page navigates away on success, and
    // a load of the index replaces what it shows.
    licenceDeleteSucceeded: (state) => {
      state.licenceDelete = { status: 'succeeded', error: null };
    },
    licenceDeleteFailed: (state, action: PayloadAction<string>) => {
      state.licenceDelete = { status: 'failed', error: action.payload };
    },
    /** Acknowledge a settled deletion (the hook calls it after the snackbar). */
    licenceDeleteCleared: (state) => {
      state.licenceDelete = emptyLicenceDelete;
    },

    // ── Main contacts lifecycle (#564) ──
    contactsSaveStarted: (state) => {
      state.contactsSave = { status: 'pending', error: null };
    },
    /** The set the backend now holds replaces the loaded one; no reload. */
    contactsSaveSucceeded: (state, action: PayloadAction<PrimaryContact[]>) => {
      if (state.licence) state.licence.primary_contacts = action.payload;
      state.contactsSave = { status: 'succeeded', error: null };
    },
    contactsSaveFailed: (state, action: PayloadAction<string>) => {
      state.contactsSave = { status: 'failed', error: action.payload };
    },
    /** Acknowledge a settled save (the contacts hook calls it after the snackbar). */
    contactsSaveCleared: (state) => {
      state.contactsSave = emptyContactsSave;
    },

    // ── Credits lifecycle ──
    // Like `loadStarted`, this keeps the current balance + events so the refetch that
    // follows a grant never blanks the chart and the table mid-flight.
    creditsLoadStarted: (state) => {
      state.credits.loadStatus = 'loading';
      state.credits.error = null;
    },
    creditsLoadSucceeded: (state, action: PayloadAction<CreditHistory>) => {
      state.credits.balance = action.payload.balance;
      state.credits.events = action.payload.events;
      state.credits.loadStatus = 'loaded';
      state.credits.error = null;
    },
    creditsLoadFailed: (state, action: PayloadAction<CreditsError>) => {
      state.credits.loadStatus = 'failed';
      state.credits.error = action.payload;
    },

    // ── Grant lifecycle ──
    grantStarted: (state) => {
      state.grant = { status: 'pending', error: null };
    },
    grantSucceeded: (state) => {
      state.grant = { status: 'succeeded', error: null };
    },
    grantFailed: (state, action: PayloadAction<CreditsError>) => {
      state.grant = { status: 'failed', error: action.payload };
    },
    /** Acknowledge a settled grant (the dialog hook calls it after the snackbar). */
    grantCleared: (state) => {
      state.grant = emptyGrant;
    },

    // ── Activity feed lifecycle (#566) ──
    // A new licence starts from nothing; the same licence keeps what it shows, so
    // re-opening the Overview refreshes the feed without flashing the skeleton.
    activityLoadStarted: (state, action: PayloadAction<string>) => {
      if (state.activity.licenceId !== action.payload) {
        state.activity = { ...emptyActivity, licenceId: action.payload };
      }
      state.activity.status = 'loading';
      state.activity.moreStatus = 'idle';
      state.activity.error = null;
    },
    activityLoadSucceeded: (state, action: PayloadAction<ActivityPageLoaded>) => {
      if (state.activity.licenceId !== action.payload.licenceId) return;
      state.activity.items = action.payload.page.items;
      state.activity.nextCursor = action.payload.page.next_cursor;
      state.activity.status = 'loaded';
    },
    activityLoadFailed: (state, action: PayloadAction<ActivityPageFailed>) => {
      if (state.activity.licenceId !== action.payload.licenceId) return;
      state.activity.status = 'failed';
      state.activity.error = action.payload.message;
    },
    activityMoreStarted: (state) => {
      state.activity.moreStatus = 'loading';
      state.activity.error = null;
    },
    // The next page lands only on the feed that asked for it: another licence's, or
    // one a first-page reload has replaced since (`moreStatus` back to idle), would
    // append events that are not (or no longer) the tail of what is shown.
    activityMoreSucceeded: (state, action: PayloadAction<ActivityPageLoaded>) => {
      if (!isAwaitingMore(state.activity, action.payload.licenceId)) return;
      state.activity.items.push(...action.payload.page.items);
      state.activity.nextCursor = action.payload.page.next_cursor;
      state.activity.moreStatus = 'idle';
    },
    activityMoreFailed: (state, action: PayloadAction<ActivityPageFailed>) => {
      if (!isAwaitingMore(state.activity, action.payload.licenceId)) return;
      state.activity.moreStatus = 'failed';
      state.activity.error = action.payload.message;
    },

    // ── Contract history (#750) ──
    // Same licence: keep what is shown while it refreshes (after an edit).
    contractHistoryLoadStarted: (state, action: PayloadAction<string>) => {
      if (state.contractHistory.licenceId !== action.payload) {
        state.contractHistory = { ...emptyContractHistory, licenceId: action.payload };
      }
      state.contractHistory.status = 'loading';
      state.contractHistory.error = null;
    },
    contractHistoryLoadSucceeded: (state, action: PayloadAction<ContractHistoryLoaded>) => {
      if (state.contractHistory.licenceId !== action.payload.licenceId) return;
      state.contractHistory.history = action.payload.history;
      state.contractHistory.status = 'loaded';
    },
    contractHistoryLoadFailed: (state, action: PayloadAction<NotesFailed>) => {
      if (state.contractHistory.licenceId !== action.payload.licenceId) return;
      state.contractHistory.status = 'failed';
      state.contractHistory.error = action.payload.message;
    },

    // ── Notes tab (#605) ──
    // A new licence starts from nothing; the same licence keeps what it shows, so
    // re-opening the tab refreshes the notes without flashing the spinner.
    notesLoadStarted: (state, action: PayloadAction<string>) => {
      if (state.notes.licenceId !== action.payload) {
        state.notes = { ...emptyNotes, licenceId: action.payload };
      }
      state.notes.status = 'loading';
      state.notes.error = null;
    },
    notesLoadSucceeded: (state, action: PayloadAction<NotesLoaded>) => {
      if (state.notes.licenceId !== action.payload.licenceId) return;
      state.notes.notes = action.payload.notes;
      state.notes.status = 'loaded';
    },
    notesLoadFailed: (state, action: PayloadAction<NotesFailed>) => {
      if (state.notes.licenceId !== action.payload.licenceId) return;
      state.notes.status = 'failed';
      state.notes.error = action.payload.message;
    },
    notesSaveStarted: (state) => {
      state.notes.save = { status: 'pending', error: null };
    },
    // The notes as the server saved them (normalised, author and date) replace the
    // shown ones; only on the licence they belong to.
    notesSaveSucceeded: (state, action: PayloadAction<NotesLoaded>) => {
      state.notes.save = { status: 'succeeded', error: null };
      if (state.notes.licenceId !== action.payload.licenceId) return;
      state.notes.notes = action.payload.notes;
      state.notes.status = 'loaded';
    },
    notesSaveFailed: (state, action: PayloadAction<string>) => {
      state.notes.save = { status: 'failed', error: action.payload };
    },
    notesSaveCleared: (state) => {
      state.notes.save = emptyNotes.save;
    },

    // ── Lists tab actions (#577) ──
    // A deletion drops the rows, a status change or an approval request swaps the
    // row for the one the server returns: the status bar, the tab badge and the
    // filter all read `lists`, so they follow without a reload.
    listMutationStarted: (state, action: PayloadAction<ListMutationKind>) => {
      state.listMutation = { kind: action.payload, status: 'pending', error: null, copy: null };
    },
    listsDeleteSucceeded: (state, action: PayloadAction<string[]>) => {
      const gone = new Set(action.payload);
      state.lists = state.lists.filter((l) => !gone.has(l.id));
      state.listMutation.status = 'succeeded';
    },
    listUpdateSucceeded: (state, action: PayloadAction<ListResponse>) => {
      state.lists = state.lists.map((l) => (l.id === action.payload.id ? action.payload : l));
      state.listMutation.status = 'succeeded';
    },
    // The copy joins the rows in the order the load gives them, so it shows up in
    // the table, the status bar and the tab badge without a reload (#662).
    listDuplicateSucceeded: (state, action: PayloadAction<ListResponse>) => {
      state.lists = sortListsByRecency([...state.lists, action.payload]);
      state.listMutation.status = 'succeeded';
      state.listMutation.copy = { id: action.payload.id, name: action.payload.name };
    },
    listMutationFailed: (state, action: PayloadAction<string>) => {
      state.listMutation.status = 'failed';
      state.listMutation.error = action.payload;
    },
    listMutationCleared: (state) => {
      state.listMutation = emptyListMutation;
    },

    approversLoadStarted: (state) => {
      state.approvers.status = 'loading';
    },
    approversLoadSucceeded: (state, action: PayloadAction<Approver[]>) => {
      state.approvers = { items: action.payload, status: 'loaded' };
    },
    approversLoadFailed: (state) => {
      state.approvers.status = 'failed';
    },

    // ── Customer Success Manager candidates (#583) ──
    csmCandidatesLoadStarted: (state) => {
      state.csmCandidates.status = 'loading';
    },
    csmCandidatesLoadSucceeded: (state, action: PayloadAction<LicenceCsm[]>) => {
      state.csmCandidates = { items: action.payload, status: 'loaded' };
    },
    csmCandidatesLoadFailed: (state) => {
      state.csmCandidates.status = 'failed';
    },

    // ── Client categories (#604); the form picks an added one itself (its draft is local) ──
    clientCategoriesLoadStarted: (state) => {
      clientCategoriesTransitions.loadStarted(state.clientCategories);
    },
    clientCategoriesLoadSucceeded: (state, action: PayloadAction<ClientCategory[]>) => {
      clientCategoriesTransitions.loadSucceeded(state.clientCategories, action.payload);
    },
    clientCategoriesLoadFailed: (state) => {
      clientCategoriesTransitions.loadFailed(state.clientCategories);
    },
    clientCategoryAddStarted: (state) => {
      clientCategoriesTransitions.addStarted(state.clientCategories);
    },
    clientCategoryAddSucceeded: (state, action: PayloadAction<ClientCategory>) => {
      clientCategoriesTransitions.addSucceeded(state.clientCategories, action.payload);
    },
    clientCategoryAddFailed: (state, action: PayloadAction<string>) => {
      clientCategoriesTransitions.addFailed(state.clientCategories, action.payload);
    },
    clientCategoryAddCleared: (state) => {
      clientCategoriesTransitions.addCleared(state.clientCategories);
    },

    /** Optimistically flip bulk-invited users to PENDING (after InvitationDialog send). */
    usersMarkedSent: (state, action: PayloadAction<string[]>) => {
      state.users = markUsersAsSent(state.users, action.payload);
    },

    /** Reset everything to the initial state (on mount + unmount). */
    resetForm: () => initialState,
  },
});

export const licenceDetailActions = slice.actions;
export const licenceDetailReducer = slice.reducer;
